// Left tool rail for the Designer, extracted verbatim from Designer.tsx.
// Presentational: tool selector buttons (Products/Add Text via ToolBtn) plus
// Add Design / Studio / My Library / Delete actions, all forwarded via props.
import type { Tool } from './helpers'
import ToolBtn from './ToolBtn'

export interface DesignerToolNavProps {
  tool: Tool
  setTool: (t: Tool) => void
  setAddDesignOpen: (v: boolean) => void
  setStudioMediaOpen: (v: boolean) => void
  setLibraryOpen: (v: boolean) => void
  deleteSelected: () => void
  hasSelection: boolean
}

export default function DesignerToolNav({
  tool, setTool, setAddDesignOpen, setStudioMediaOpen, setLibraryOpen, deleteSelected, hasSelection,
}: DesignerToolNavProps) {
  return (
    <nav className="bg-ink w-[78px] flex-shrink-0 border-r border-white/5 flex flex-col py-2">
      <ToolBtn id="product" label="Products" tool={tool} setTool={setTool}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M20.38 3.46L16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z"/></svg>
      </ToolBtn>
      <ToolBtn id="text" label="Add Text" tool={tool} setTool={setTool}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>
      </ToolBtn>
      {/* Add Design — opens the AddDesignModal popup instead of just
          switching the right-sidebar tool. Two tabs inside: Upload
          (file picker + drag-drop) and Generate with AI (Nano
          Banana 2). Falls back to switching tool only on right-
          click for power users who want the sidebar Upload zone. */}
      <button onClick={() => setAddDesignOpen(true)}
        className="flex flex-col items-center justify-center gap-1.5 w-full py-3 px-2 text-[10px] uppercase tracking-wider font-semibold border-l-2 border-transparent text-white/55 hover:text-white hover:bg-white/5 transition-colors"
        title="Add a design — upload a file or generate one with AI">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
        Add Design
      </button>
      {/* Studio assets + My Library — two siblings in the nav. No section
          heading; the icon/label rhythm carries the grouping on its own. */}
      <button onClick={() => setStudioMediaOpen(true)}
        className="flex flex-col items-center justify-center gap-1.5 w-full py-3 px-2 text-[10px] uppercase tracking-wider font-semibold border-l-2 border-transparent text-white/55 hover:text-white hover:bg-white/5 transition-colors"
        title="Studio media library — designer-curated assets">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z"/>
        </svg>
        Studio
      </button>
      {/* My Library — always visible. /designer is auth-gated so every
          user reaching this surface is signed in; the in-browser AuthKit
          `user` can briefly be null during hydration even when the
          server cookie is valid, so don't gate the button on it. */}
      <button onClick={() => setLibraryOpen(true)}
        className="flex flex-col items-center justify-center gap-1.5 w-full py-3 px-2 text-[10px] uppercase tracking-wider font-semibold border-l-2 border-transparent text-white/55 hover:text-white hover:bg-white/5 transition-colors"
        title="My Library — your saved designs + uploaded artwork">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 12l9 4 9-4"/><path d="M3 17l9 4 9-4"/>
        </svg>
        My Library
      </button>
      <button onClick={deleteSelected} disabled={!hasSelection}
        className={`flex flex-col items-center justify-center gap-1.5 w-full py-3 px-2 text-[10px] uppercase tracking-wider font-semibold border-l-2 border-transparent transition-colors ${hasSelection ? 'text-white/55 hover:text-white hover:bg-white/5' : 'text-white/20 cursor-not-allowed'}`}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
        Delete
      </button>
    </nav>
  )
}
